import React, { useState } from 'react';
import { useScenario } from '../../context/ScenarioContext';
import { useProducts } from '../../context/ProductsContext';
import { useCalculator } from '../../context/CalculatorContext';
import { useToast } from '../Common/Toast';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';

const ScenarioManager = () => {
  const { scenarios, saveScenario, deleteScenario, loading } = useScenario();
  const { products, deleteAllProducts, addProduct } = useProducts();
  const { inputs, results, updateInputs } = useCalculator();
  const { showToast } = useToast();
  
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) {
      showToast('Please enter a scenario name', 'error');
      return;
    }

    setSaving(true);
    const scenarioData = {
      products: products.map(p => ({
        name: p.name,
        baseHours: p.baseHours,
        comp: p.comp,
        risk: p.risk,
        env: p.env,
        enabled: p.enabled
      })),
      inputs,
      calculatedModel: results
    };

    const result = await saveScenario(scenarioName, scenarioDescription, scenarioData);
    setSaving(false);

    if (result.success) {
      showToast(`Scenario "${scenarioName}" saved successfully!`);
      setScenarioName('');
      setScenarioDescription('');
      setShowSaveForm(false);
    } else {
      showToast('Failed to save scenario', 'error');
    }
  };

  const handleLoadScenario = async (scenario) => {
    if (!window.confirm(`Load scenario "${scenario.name}"? This will replace your current data.`)) {
      return;
    }

    try {
      // Clear existing products
      await deleteAllProducts();

      // Wait a moment for deletion to complete
      setTimeout(async () => {
        // Load scenario products
        for (const product of scenario.data.products) {
          await addProduct(product);
        }

        // Load inputs
        updateInputs(scenario.data.inputs);

        showToast(`Scenario "${scenario.name}" loaded successfully!`);
      }, 500);
    } catch (err) {
      showToast('Failed to load scenario', 'error');
      console.error(err);
    }
  };

  const handleDeleteScenario = async (scenario) => {
    if (!window.confirm(`Delete scenario "${scenario.name}"?`)) {
      return;
    }

    const result = await deleteScenario(scenario.id);
    if (result.success) {
      showToast(`Scenario "${scenario.name}" deleted successfully!`);
    } else {
      showToast('Failed to delete scenario', 'error');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">🎯 Scenario Management</h2>
          <Button onClick={() => setShowSaveForm(!showSaveForm)}>
            {showSaveForm ? 'Cancel' : '💾 Save Current Scenario'}
          </Button>
        </div>

        {showSaveForm && (
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <h3 className="font-bold text-lg mb-4">Save Current Scenario</h3>
            <div className="space-y-4">
              <Input
                label="Scenario Name"
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g., Budget Proposal 2024"
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={scenarioDescription}
                  onChange={(e) => setScenarioDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Brief description of this scenario..."
                />
              </div>
              <Button onClick={handleSaveScenario} disabled={saving} fullWidth>
                {saving ? 'Saving...' : 'Save Scenario'}
              </Button>
            </div>
          </div>
        )}

        <div className="text-sm text-gray-600 mb-4">
          Save and load different workforce planning scenarios to compare approaches.
        </div>
      </Card>

      <Card title="📂 Saved Scenarios">
        {loading ? (
          <div className="text-center py-8 text-gray-600">Loading scenarios...</div>
        ) : scenarios.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            No saved scenarios yet. Save your current configuration above to get started!
          </div>
        ) : (
          <div className="space-y-3">
            {scenarios.map(scenario => (
              <div
                key={scenario.id}
                className="border border-gray-300 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">{scenario.name}</h3>
                    {scenario.description && (
                      <p className="text-sm text-gray-600 mt-1">{scenario.description}</p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">
                      Saved: {formatDate(scenario.timestamp)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleLoadScenario(scenario)}
                    >
                      Load
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteScenario(scenario)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm mt-3 pt-3 border-t">
                  <div>
                    <span className="text-gray-600">Products:</span>
                    <span className="ml-2 font-medium">
                      {scenario.data.products?.length || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">FTE:</span>
                    <span className="ml-2 font-medium">
                      {scenario.data.calculatedModel?.totalFTEs?.toFixed(2) || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Employees:</span>
                    <span className="ml-2 font-medium">
                      {scenario.data.inputs?.employeeCount || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ScenarioManager;
